import React from 'react'
import { arrayOf, bool, func, number, object } from 'prop-types'
import DefaultHeader, { CustomFilterTypes } from './Headers/DefaultHeader'
import EditingHeader from './Headers/EditingHeader'
import DashboardBody from './DashboardBody'
import DashboardMessage from './Notifcations/DashboardMessage'
import { Widget } from './Widget'
import { Container, Icon, Loader, Message } from 'semantic-ui-react'
import css from './Dashboard.css'
import { generateId, isString } from './Utils'
import WidgetForm from './WidgetForm'
import { toast } from './Notifcations/Toast'
import { translate } from 'react-i18next'
import { findWidget } from './WidgetCollections'

let scrub = (layouts, widget) => {
  let newLayout = {}
  Object.keys(layouts).forEach(layout => {
    newLayout[layout] = layouts[layout].filter(entry => entry.i !== widget.id)
  })
  return newLayout
}

let CommonProps = {
  widgetTypes: arrayOf(object),
  layoutStorage: object.isRequired,
  messageDisplayTimeMillis: number,
  style: object,
  fluid: bool,
  filters: CustomFilterTypes
}

let CommonPropDefaults = {
  widgetTypes: [],
  className: 'layout',
  cols: { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 },
  rowHeight: 45,
  messageDisplayTimeMillis: 5000,
  fluid: false,
  filters: [
    {
      name: 'organization',
      values: [
        { text: 'Organization 1', value: 'org1' },
        { text: 'Organization 2', value: 'org2' },
        { text: 'Organization 3', value: 'org3' },
        { text: 'Organization 4', value: 'org4' }]
    }, {
      name: 'timeRange',
      values: [
        'TODAY',
        'YESTERDAY',
        'LAST_WEEK',
        'LAST_MONTH',
        'LAST_YEAR'
      ]
    }
  ]
}

const Error = translate()(({size, header, list, t}) => {
  let readable = entry => {
    if (isString(entry)) {
      return entry
    }
    if (entry.message) {
      return entry.message
    }
    return JSON.stringify(entry)
  }
  let readableList = list.map(readable)
  return <Message
    error
    size={size}
    header={t(header)}
    list={readableList}
  />
})

/**
 * Dashboard is the highest level component of the framework, and carries the state of the dashboard.
 * It is the classes responsibility to organise where things are saved to.
 */
class Dashboard extends React.PureComponent {
  state = {
    rollback: {},
    // The current layout of the db.
    layouts: {},
    // Widgets are react classes.
    widgets: [], // array of Widget
    bannerDisplay: false,
    editing: false,
    loading: true,
    saving: false,
    loadFailed: false,
    loadingFailedMsg: [],
    loadingFailedCode: 0
  };

  constructor(params) {
    super(params)
    this.startEdit = this.startEdit.bind(this)
    this.cancelEdit = this.cancelEdit.bind(this)
    this.saveEdits = this.saveEdits.bind(this)
    this.addMessage = this.addMessage.bind(this)
    this.addMessageClass = this.addMessageClass.bind(this)
    this.addWidget = this.addWidget.bind(this)
    this.onLayoutChange = this.onLayoutChange.bind(this)
    this.onWidgetRemove = this.onWidgetRemove.bind(this)
    let { messageDisplayTimeMillis } = this.props
    this.toast = toast({messageDisplayTimeMillis})
  }

  static propTypes = {
    ...CommonProps,
    onFilterChange: func,
    filter: object
  };

  static defaultProps = {
    ...CommonPropDefaults,
    onFilterChange: () => { },
    filter: {}
  };

  addMessageClass(message, type, cls, params) {
    this.toast({
      message,
      type,
      params,
      id: cls
    })
  }

  addMessage(message, type, params) {
    this.toast({
      message,
      type,
      params
    })
  };

  startEdit() {
    this.setState({
      editing: true,
      rollback: {
        layouts: {
          ...this.state.layouts
        },
        widgets: [
          ...this.state.widgets
        ]
      }
    })
  }

  cancelEdit() {
    this.setState(prev => ({
      editing: false,
      layouts: prev.rollback.layouts,
      widgets: prev.rollback.widgets,
      rollback: {}
    }))
  }

  saveEdits() {
    this.addMessageClass('messages.saving', 'info', 'Saving')
    this.setState({
      editing: false,
      saving: true
    }, () => {
      this.props.layoutStorage.save({
        layouts: this.state.layouts,
        widgets: this.state.widgets
      })
        .then(() => this.addMessageClass('messages.saved', 'success', 'Saving'))
        .catch(() => this.addMessage('messages.saveError', 'error'))
        .finally(() => this.setState({ saving: false }))
    })
  }

  load = () => {
    this.props.layoutStorage.load().then(config => {
      let matchedW = config.widgets.map(findWidget(this.props.widgetTypes)) || []
      this.setState({
        layouts: config.layouts,
        widgets: matchedW,
        loading: false,
        loadFailed: false,
        rollback: {
          layouts: config.layouts,
          widgets: matchedW
        }
      })
    }).catch(error => {
      console.log(error)
      this.setState({
        loading: false,
        loadFailed: true,
        loadingFailedMsg: [error]
      })
    })
  };

  componentDidUpdate(prev) {
    if (!prev.layoutStorage.equals(this.props.layoutStorage)) {
      this.load()
    }
  }

  componentDidMount() {
    this.setState({ bannerDisplay: true })
    this.load()
  }

  onLayoutChange(layout, layouts) {
    if (this.state.editing) {
      this.setState({ layouts })
    }
  }

  addWidget(selectionChange) {
    if (this.state.editing) {
      let {added, removed} = selectionChange
      let { widgets } = this.state
      const removedW = removed.map(d => widgets.find(w => w.matchesDefinition(d)))
      // Not the most ideal in long term, will trigger a render for each removed widget.
      removedW.forEach(this.onWidgetRemove)
      let more = added.map(clazz => {
        return new Widget(generateId(), clazz)
      })
      let wasNotRemoved = w => w && !removedW.some(r => r.id === w.id)
      this.setState(prev => {
        let withoutRemoved = prev.widgets.filter(wasNotRemoved)
        let widgets = [
          ...withoutRemoved,
          ...more
        ]
        return {
          widgets: widgets
        }
      }, () => {
        more.length > 1 && this.addMessage('messages.addedWidgets', 'info', {count: more.length})
        removedW.length > 1 && this.addMessage('messages.removedWidgets', 'info', {count: removedW.length})
      })
    }
  }

  onWidgetRemove(widget) {
    this.setState(prev => {
      let widgets = prev.widgets.filter(w => w.id !== widget.id)
      let newLayouts = scrub(this.state.layouts, widget)
      return {
        widgets: widgets,
        layouts: newLayouts
      }
    })
  }

  handleDismiss = () => {
    this.setState({ bannerDisplay: false })
  }

  render() {
    let { widgets, editing, loading, bannerDisplay, loadFailed, saving, loadingFailedMsg } = this.state
    // data props
    let { widgetTypes, widgetSections, filter, onFilterChange, filters } = this.props
    // style props
    let { fluid, style, welcomeBanner, bannerMessage } = this.props
    return <Container fluid={fluid} style={style} className={css.container}>
      {widgets.length === 0 && bannerDisplay && !loading && !editing && welcomeBanner &&
      <Message info onDismiss={this.handleDismiss} style={{
        boxShadow: 'none'
      }}>
        <p>
          <Icon name='polycom nfo circle fill' />
          {bannerMessage}
        </p>
      </Message>}
      {loadFailed && <Error
        size='large'
        header='messages.loadingFailed'
        list={loadingFailedMsg}
      />}
      {editing
        ? <EditingHeader
          onSave={this.saveEdits}
          onCancel={this.cancelEdit}>
          <WidgetForm
            widgetTypes={widgetTypes}
            widgetSections={widgetSections}
            currentWidgets={widgets}
            onSelectionChange={this.addWidget} />
        </EditingHeader>
        : <DefaultHeader
          loading={saving || loading}
          startEditing={this.startEdit}
          filters={DefaultHeader.CustomFilters({
            onFilterChange: onFilterChange,
            filter: filter,
            filters: filters
          })} />
      }

      <Loader active={loading} inline='centered' />
      <DashboardMessage />
      {!loadFailed && <DashboardBody
        key='body'
        widgets={widgets}
        layouts={this.state.layouts}
        editing={editing}
        filter={filter}
        onLayoutChange={this.onLayoutChange}
        onWidgetRemove={this.onWidgetRemove}
        onSave={this.saveEdits}
      />}
    </Container>
  }
}

class SelfFilter extends React.Component {
  static propTypes = {
    ...CommonProps
  };

  static defaultProps = {
    ...CommonPropDefaults
  };
  state = {
    filter: {}
  };
  updateFilter = (newFilter) => {
    this.setState({
      filter: newFilter
    })
  };
  render() {
    let {onFilterChange, filter, ...props} = this.props
    let ignore = () => {}
    ignore(onFilterChange)
    ignore(filter)
    return <Dashboard {...props} onFilterChange={(filter) => this.updateFilter(filter)} filter={this.state.filter} />
  }
}

export default SelfFilter
export { scrub, Dashboard}
